import React, { useState, useEffect } from 'react';
import './styles/global.css';
import './styles/mui-overrides.css';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  useMediaQuery,
  Drawer,
  Fab
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Header from './components/layout/Header';
import LeftPanel from './components/layout/LeftPanel';
import MainContent from './components/layout/MainContent';
import RightPanel from './components/layout/RightPanel';
import Login from './components/auth/Login';
import { Trip } from './types/trip';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store/store';
import { restoreAuth } from './store/slices/authSlice';
import theme from './theme';
import format from 'date-fns/format';
import {fetchActiveNotifications,processActiveNotifications} from './store/slices/tripNotification';
import { AppDispatch } from './store/store';

function App() {
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
  const [openTrips, setOpenTrips] = useState<Trip[]>([]);
  const [activeTab, setActiveTab] = useState('');
  const [pollingCycle, setPollingCycle] = useState(0);
  const justClosedNotifications = React.useRef(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const notifications = useSelector((state: RootState) => state.tripNotifications?.notifications || []);

  // Constants for repeated styles
  const fabStyle = {
    position: 'absolute',
    zIndex: 1200,
    bgcolor: '#2a2a2a',
    '&:hover': { bgcolor: '#3a3a3a' },
    borderRadius: '50%',
  };

 useEffect(() => {
  if(!isAuthenticated) return;

  const fetchNotifications = async () => {
    const currentDateTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
    try{
      await dispatch(processActiveNotifications(currentDateTime));
      await dispatch(fetchActiveNotifications(currentDateTime));
      // Reset the manual close flag on each polling cycle and trigger re-check
      justClosedNotifications.current = false;
      setPollingCycle(prev => prev + 1);
    }catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };
  fetchNotifications();

  // Set up interval to call every 5 minutes
  const intervalId = setInterval(fetchNotifications, 5 * 60 * 1000);

  // Cleanup function
  return () => clearInterval(intervalId);
}, [dispatch, isAuthenticated]);

// Automatically open/close tripNotifications tab based on active notifications
useEffect(() => {
  if (!isAuthenticated) return;

  // Check if tripNotifications tab is already open
  const notificationTabExists = openTrips.find(t => 
    t.tripType === 'TRIP_NOTIFICATIONS'
  );

  // AUTO OPEN: If there are notifications and tab is not open, open it
  // Skip if we just manually closed it (wait for next polling cycle)
  if (notifications.length > 0 && !notificationTabExists && !justClosedNotifications.current) {
    const newTrip: Trip = {
      tripId: 'TripNotifications',
      trip_id: 'TripNotifications',
      tripCode: 'TripNotifications',
      tripType: 'TRIP_NOTIFICATIONS',
      operator: '',
    };
    setOpenTrips(prev => [newTrip, ...prev]);
    
    // Only set as active tab if there are no other tabs open
    if (openTrips.length === 0) {
      setActiveTab('TripNotifications');
    }
  }
  
  // AUTO CLOSE: If there are NO notifications and tab is open, close it automatically
  if (notifications.length === 0 && notificationTabExists) {
    const newTrips = openTrips.filter(t => t.tripId !== 'TripNotifications');
    setOpenTrips(newTrips);
    if (activeTab === 'TripNotifications' && newTrips.length > 0) {
      const lastTripId = newTrips[newTrips.length - 1].tripId || newTrips[newTrips.length - 1].trip_id;
      setActiveTab(lastTripId || '');
    } else if (newTrips.length === 0) {
      setActiveTab('');
    }
  }
}, [notifications.length, isAuthenticated, openTrips, activeTab, pollingCycle]);

  // Check for saved auth data on app load
  useEffect(() => {
    const savedAuth = localStorage.getItem('auth');
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        if (authData.token && authData.user) {
          dispatch(restoreAuth({ token: authData.token, user: authData.user }));
        }
      } catch (error) {
        console.error('Failed to parse saved auth data:', error);
        localStorage.removeItem('auth');
      }
    }
  }, [dispatch]);

 const toggleDrawer = (side: 'left' | 'right') => {
    if (side === 'left') {
      // If opening left panel, close right panel
      if (!leftDrawerOpen) {
        setRightDrawerOpen(false);
      }
      setLeftDrawerOpen(!leftDrawerOpen);
    } else {
      // If opening right panel, close left panel
      if (!rightDrawerOpen) {
        setLeftDrawerOpen(false);
      }
      setRightDrawerOpen(!rightDrawerOpen);
    }
  };

  const handleTripClick = (trip: any) => {
    // Handle closing a specific tab before opening new one
    if (trip.closeTabId) {      const newTrips = openTrips.filter(t => t.tripId !== trip.closeTabId && t.trip_id !== trip.closeTabId);
      setOpenTrips(newTrips);
    }
    
    // Check if this is a tab replacement request
    if (trip.replaceTabId) {
      
      // Find the index of the tab to replace
      const tabIndex = openTrips.findIndex(t => 
        t.tripId === trip.replaceTabId || t.trip_id === trip.replaceTabId
      );
      
      if (tabIndex !== -1) {
        // Create new trip object with proper ID - ensure we completely replace the object
        const newTripData = {
          tripId: trip.tripCode, // Use tripCode as the primary ID
          trip_id: trip.tripCode, // Also set for backward compatibility
          tripCode: trip.tripCode,
          tripType: 'TRIP_DETAILS',
          carType: '',
          carModel: '',
          operator: '',
          source: trip.sourceCity || '',
          destination: trip.destinationCities || '',
          status: trip.tripStatus || 'PENDING'
        };
        
          // Force a complete state update by creating a new array
        const updatedTrips = [...openTrips];
        updatedTrips[tabIndex] = newTripData;
        
        setOpenTrips(updatedTrips);
        
        // Immediately set the active tab without delay to prevent sequential tab changes
        setActiveTab(trip.tripCode);
        
        return;
      } else {
      }
    }
    
    // Check if trip already exists in tabs
    const existingTrip = openTrips.find(t => 
      (t.tripId === trip.tripId) || 
      (t.trip_id === trip.tripId) ||
      (t.tripId === trip.tripCode) ||
      (t.trip_id === trip.tripCode)
    );
    
    if (!existingTrip) {
      // Add new trip to tabs
      const newTrip = {
        tripId: trip.tripId || trip.tripCode,
        trip_id: trip.tripId || trip.tripCode,
        tripCode: trip.tripCode,
        tripType: trip.tripType || 'TRIP_DETAILS',
        carType: trip.carType || '',
        carModel: trip.carModel || '',
        operator: trip.operator || '',
        ...trip
      };
      setOpenTrips(prev => [...prev, newTrip]);
    }
    
    // Set active tab to the trip ID or trip code
    setActiveTab(trip.tripId || trip.tripCode);
  };

  const handleCloseTab = (tripId: string) => {
    // Check if closing the notification tab manually
    const closingTrip = openTrips.find(t => t.tripId === tripId);
    if (closingTrip?.tripType === 'TRIP_NOTIFICATIONS') {
      justClosedNotifications.current = true;
    }
    
    const newTrips = openTrips.filter(t => t.tripId !== tripId);
    setOpenTrips(newTrips);
    if (activeTab === tripId && newTrips.length > 0) {
      const lastTripId = newTrips[newTrips.length - 1].tripId || newTrips[newTrips.length - 1].trip_id;
      setActiveTab(lastTripId || '');
    } else if (newTrips.length === 0) {
      setActiveTab('');
    }
  };

  const renderToggleButton = (side: 'left' | 'right', isOpen: boolean, isInside = false) => {
    const Icon = side === 'left' 
      ? (isOpen ? ChevronLeftIcon : ChevronRightIcon)
      : (isOpen ? ChevronRightIcon : ChevronLeftIcon);
    
    const position = isInside 
      ? { [side === 'left' ? 'right' : 'left']: -12, top: 50 }
      : { [side]: 10, top: 50 };

    return (
      <Fab
        color="default"
        size="small"
        onClick={() => toggleDrawer(side)}
        sx={{ ...fabStyle, ...position }}
      >
        <Icon sx={{ color: '#aaaaaa', fontSize: '1.2rem' }} />
      </Fab>
    );
  };

  const renderPanel = (side: 'left' | 'right', isOpen: boolean, PanelComponent: React.ComponentType<any>) => {
    // const props = side === 'left' ? { onTripClick: handleTripClick } : {};
    const props = {onTripClick: handleTripClick}
    
    return isMobile ? (
      <Drawer
        anchor={side}
        open={isOpen}
        onClose={() => toggleDrawer(side)}
        sx={{ '& .MuiDrawer-paper': { width: 300, backgroundColor: '#252526' } }}
      >
        <PanelComponent {...props} />
      </Drawer>
    ) : (
      <Box sx={{ display: isOpen ? 'flex' : 'none', height: '100%', position: 'relative' }}>
        <PanelComponent {...props} />
        {isOpen && renderToggleButton(side, isOpen, true)}
      </Box>
    );
  };

  const handleAddTrip = (trip: any) => {
    
    // Check if trip already exists
    const existingTrip = openTrips.find(t => 
      (t.tripId === trip.tripId) || 
      (t.trip_id === trip.tripId) ||
      (t.tripId === trip.tripCode) ||
      (t.trip_id === trip.tripCode)
    );
    
    if (!existingTrip) {
      setOpenTrips(prev => [...prev, trip]);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Header
            onMenuClick={() => toggleDrawer('left')}
            onSidebarClick={() => toggleDrawer('right')}
            showMenuButton={isMobile}
          />

          <Routes>
            <Route path="" element={
              !isAuthenticated ? (
                <Login />
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexGrow: 1,
                  overflow: 'hidden',
                  p: 1,
                  gap: 1.5,
                  bgcolor: '#1e1e1e',
                  height: '100%',
                  position: 'relative',
                }}>
                  {/* Left Panel */}
                  {renderPanel('left', leftDrawerOpen, LeftPanel)}
                  
                  {/* Toggle buttons for closed panels */}
                  {!leftDrawerOpen && !isMobile && renderToggleButton('left', false)}

                  {/* Main Content */}
                  <Box sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    <MainContent
                      openTrips={openTrips}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      onCloseTab={handleCloseTab}
                      onAddTrip={handleAddTrip}
                    />
                  </Box>

                  {/* Right Panel */}
                  {renderPanel('right', rightDrawerOpen, RightPanel)}
                  
                  {!rightDrawerOpen && !isMobile && renderToggleButton('right', false)}

                  {/* Mobile toggle buttons */}
                  {isMobile && (
                    <>
                      <Fab
                        color="default"
                        size="small"
                        onClick={() => toggleDrawer('left')}
                        sx={{
                          ...fabStyle,
                          left: leftDrawerOpen ? 310 : 10,
                          top: 10,
                          transition: 'left 0.3s',
                        }}
                      >
                        {leftDrawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                      </Fab>
                      <Fab
                        color="default"
                        size="small"
                        onClick={() => toggleDrawer('right')}
                        sx={{
                          ...fabStyle,
                          right: rightDrawerOpen ? 310 : 10,
                          top: 10,
                          transition: 'right 0.3s',
                        }}
                      >
                        {rightDrawerOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                      </Fab>
                    </>
                  )}
                </Box>
              )
            } />
          </Routes>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
